import { useEffect, useState } from 'react';
import { Dropdown, Navbar, NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  INTEGRATION_CONNECTION_STATE_CONNECTED,
  INTEGRATION_CONNECTION_STATE_UNSTABLE,
} from '../../../constants.ts';
import { Territory } from '../../../schema/Territory.ts';
import { useAllTerritories } from '../../../services/Territory.ts';
import { BLACKLISTED_SELECT_HEADER_PATH } from '../../../utils/headerConstants.ts';
import { getFlagName, getToken, removeToken } from '../../../utils/utils.ts';
import { AlertDrawer } from '../../Dashboard/Alert/AlertDrawer.tsx';
import { SearchableSelect } from '../../ui/Select.tsx';

export function Header() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const [localTime, setLocalTime] = useState(new Date().toLocaleTimeString());
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const { data: territories } = useAllTerritories();
  const activeTerritories =
    territories?.filter(
      (territory: Territory) =>
        territory.integration_state_counts[INTEGRATION_CONNECTION_STATE_CONNECTED] > 0 ||
        territory.integration_state_counts[INTEGRATION_CONNECTION_STATE_UNSTABLE] > 0,
    ) || [];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setCurrentLang(lng);
  };

  useEffect(() => {
    const interval = setInterval(() => setLocalTime(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(interval);
  }, []);

  const updateValue = (territorySlug: string) => {
    navigate(`/dashboard/${territorySlug}/`);
    globalThis.location.reload();
  };
  const redirectDashboard = (territorySlug: string) => {
    navigate(`/dashboard/${territorySlug}/`);
  };
  const redirectMissionHistory = (territorySlug: string) => {
    navigate(`/mission-history/${territorySlug}/`);
  };
  const redirectAlertHistory = (territorySlug: string) => {
    navigate(`/alert-history/${territorySlug}/`);
  };
  const redirectStatistics = (territorySlug: string) => {
    navigate(`/statistics/${territorySlug}/`);
  };
  const redirectGeography = (territorySlug: string) => {
    navigate(`/geography/${territorySlug}/`);
  };
  function handleDisconnect() {
    if (getToken()) {
      removeToken();
    }
    navigate('/login/');
  }

  return (
    <Navbar className={'header justify-content-between shadow-sm'}>
      <div className={'container-fluid ms-3 me-3'}>
        <Link to={'/'}>
          <img
            src={'/theme/media/logos/logo-default.svg'}
            height="50"
            className="rounded-2 mt-2"
            alt="Padam Av logo"
          />
        </Link>
        <div className={'d-flex justify-content-between dropdown-custom-menu'}>
          <p className={'my-auto card-text me-3'}>
            {new Date().toLocaleDateString()} {localTime}
          </p>
          {params.territorySlug !== undefined && (
            <div className={'me-3 my-auto'}>
              <AlertDrawer />
            </div>
          )}
          <Dropdown align={'end'} className={'my-auto me-4'}>
            <Dropdown.Toggle className={'border-0 generic-dropdown m-0 p-0'}>
              <img
                src={`/theme/media/flags/${getFlagName(currentLang)}.png`}
                height="24"
                className="rounded-2"
                alt="Flag language"
              />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => changeLanguage('en')}>
                <div className={'d-flex'}>
                  <img
                    src={`/theme/media/flags/en.png`}
                    height="24"
                    className="rounded-2"
                    alt="UK flag"
                  />
                  <p className={'my-auto ms-2'}>English</p>
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => changeLanguage('fr')}>
                <div className={'d-flex'}>
                  <img
                    src={`/theme/media/flags/fr.png`}
                    height="24"
                    className="rounded-2"
                    alt="FR flag"
                  />
                  <p className={'my-auto ms-2'}>Français</p>
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => changeLanguage('it')}>
                <div className={'d-flex'}>
                  <img
                    src={`/theme/media/flags/it.png`}
                    height="24"
                    className="rounded-2"
                    alt="IT flag"
                  />
                  <p className={'my-auto ms-2'}>Italiano</p>
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {activeTerritories?.length > 1 &&
            !BLACKLISTED_SELECT_HEADER_PATH.includes(location.pathname) && (
              <div className={'my-auto me-3 territory-select'}>
                <SearchableSelect
                  placeholder={t('header.select-territory')}
                  options={
                    activeTerritories.map((territory: Territory) => ({
                      value: territory.slug,
                      label: territory.slug,
                    })) ?? []
                  }
                  value={params?.territorySlug}
                  onChange={(val: string) => updateValue(val)}
                />
              </div>
            )}
          <NavDropdown
            title={
              <img
                src={'/theme/media/burger.svg'}
                width="29"
                height="26"
                className="rounded-2"
                alt="Burger menu logo"
              />
            }
            className={'me-2 my-auto'}
            drop={'down'}
            align={'end'}>
            {params?.territorySlug !== undefined && (
              <>
                <Dropdown.Item
                  className={'mt-2 mb-2'}
                  onClick={() => redirectDashboard(params.territorySlug || '')}>
                  <p>
                    <img
                      src={'/theme/media/monitor.svg'}
                      width="29"
                      height="26"
                      className="rounded-2"
                      alt="Dashboard"
                    />
                    <span className={'card-text ms-1'}>{t('header.dashboard')}</span>
                  </p>
                </Dropdown.Item>
                <Dropdown.Item
                  className={'mt-2 mb-2'}
                  onClick={() => redirectStatistics(params.territorySlug || '')}>
                  <p>
                    <img
                      src={'/theme/media/statistics.svg'}
                      width="29"
                      height="26"
                      className="rounded-2"
                      alt="Dashboard"
                    />
                    <span className={'card-text ms-1'}>{t('header.statistics')}</span>
                  </p>
                </Dropdown.Item>
                <Dropdown.Item
                  className={'mt-2 mb-2'}
                  onClick={() => redirectGeography(params.territorySlug || '')}>
                  <p>
                    <img src={'/theme/media/stop.svg'} width="29" height="26" alt="Dashboard" />
                    <span className={'card-text ms-1'}>{t('header.geography')}</span>
                  </p>
                </Dropdown.Item>
                <hr className={'mt-1 mb-1 basic-hr'} />
                <Dropdown.Item
                  className={'mt-2 mb-2'}
                  onClick={() => redirectAlertHistory(params.territorySlug || '')}>
                  <p>
                    <img
                      src={'/theme/media/warning.svg'}
                      width="29"
                      height="26"
                      className="rounded-2"
                      alt="Alert icon"
                    />
                    <span className={'card-text ms-1'}>{t('header.alert-history')}</span>
                  </p>
                </Dropdown.Item>
                <Dropdown.Item
                  className={'mt-2 mb-2'}
                  onClick={() => redirectMissionHistory(params.territorySlug || '')}>
                  <p>
                    <img
                      src={'/theme/media/mission.svg'}
                      width="29"
                      height="26"
                      className="rounded-2"
                      alt="Dashboard"
                    />
                    <span className={'card-text ms-1'}>{t('header.mission-history')}</span>
                  </p>
                </Dropdown.Item>
                <hr className={'mt-1 mb-1 basic-hr'} />
              </>
            )}
            <Dropdown.Item className={'mt-2 mb-2'} onClick={() => navigate('/settings/')}>
              <p>
                <img
                  src={'/theme/media/settings-logo.svg'}
                  width="29"
                  height="26"
                  className="rounded-2"
                  alt="Dashboard"
                />
                <span className={'text-bold-grey ms-1'}>{t('header.settings')}</span>
              </p>
            </Dropdown.Item>
            <hr className={'mt-1 mb-1 basic-hr'} />
            <Dropdown.Item className={'mt-2 mb-2'} onClick={handleDisconnect}>
              <p>
                <img
                  src={'/theme/media/ArrowLineRight.svg'}
                  width="29"
                  height="26"
                  className="rounded-2"
                  alt="Logout"
                />
                <span className={'card-text text-alert ms-1'}>{t('header.logout')}</span>
              </p>
            </Dropdown.Item>
            <hr className={'mt-1 mb-1 basic-hr'} />
            <div className={'ms-3 mt-3 mb-2'}>
              <p className={'text-disabled text-collapse'}>
                Front : {import.meta.env.VITE_FRONTEND_VERSION}
              </p>
              <p className={'text-disabled text-collapse'}>
                Back : {territories?.find(() => true)?.backend_version}
              </p>
            </div>
          </NavDropdown>
        </div>
      </div>
    </Navbar>
  );
}
